import { DatePicker, Empty, Form, Modal, Select, Typography } from 'antd';

import type { ModelJournalEmployee } from '@app/api/WorkSchedule';

import { useAddWorkScheduleModal } from '../hooks';

import type { Moment } from 'moment';
import type { FC } from 'react';

import '../styles/AddWorkScheduleModal.scss';

const { Text } = Typography;

interface AddWorkScheduleModalProps {
  employeeId?: number;
  employeeName?: string;
  employees?: ModelJournalEmployee[];
  isEmployeeOptionsLoading?: boolean;
  isOpen: boolean;
  selectedDateMoment?: Moment;
  refreshCalendarData?: () => void;
  onClose(): void;
}

export const AddWorkScheduleModal: FC<AddWorkScheduleModalProps> = ({
  employeeId,
  employeeName,
  employees,
  isEmployeeOptionsLoading = false,
  isOpen,
  selectedDateMoment,
  refreshCalendarData,
  onClose,
}) => {
  const {
    form,
    isLoading,
    employeeOptions,
    isEmployeeSelectable,
    isSubmitDisabled,
    selectedEmployeeName,
    timeSelectOptions,
    handleDateChange,
    handleEmployeeChange,
    handleFinish,
    handleCancel,
  } = useAddWorkScheduleModal({
    employeeId,
    employeeName,
    employees,
    isOpen,
    selectedDateMoment,
    refreshCalendarData,
    onClose,
  });

  return (
    <Modal
      className="AddWorkScheduleModal"
      open={isOpen}
      destroyOnHidden
      width={520}
      onCancel={handleCancel}
      onOk={form.submit}
      okText="Сохранить"
      cancelText="Отмена"
      title={
        <div className="AddWorkScheduleModal__title">
          <span className="AddWorkScheduleModal__title-text">
            {isEmployeeSelectable
              ? 'Добавить исполнителя в расписание'
              : 'Добавить рабочий день в расписание'}
          </span>
          <span className="AddWorkScheduleModal__title-hint">
            {isEmployeeSelectable
              ? 'Выберите исполнителя, дату и рабочее время'
              : 'Укажите дату и рабочее время исполнителя'}
          </span>
        </div>
      }
      data-testid="calendar-add-workday-popup"
      okButtonProps={{
        loading: isLoading,
        disabled: isSubmitDisabled || isEmployeeOptionsLoading,
        'data-testid': 'save',
      }}
      cancelButtonProps={{ disabled: isLoading, 'data-testid': 'cancel' }}
    >
      <div className="AddWorkScheduleModal__content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="AddWorkScheduleModal__form"
        >
          <div className="AddWorkScheduleModal__details">
            {isEmployeeSelectable ? (
              <div className="AddWorkScheduleModal__summary-card AddWorkScheduleModal__summary-card--selector">
                <Form.Item
                  name="employeeId"
                  label="Исполнитель"
                  rules={[{ required: true, message: 'Выберите исполнителя' }]}
                  className="AddWorkScheduleModal__employee-field"
                >
                  <Select
                    showSearch
                    loading={isEmployeeOptionsLoading}
                    options={employeeOptions}
                    optionFilterProp="label"
                    placeholder="Выберите исполнителя"
                    data-testid="calendar-add-employee-select"
                    onChange={handleEmployeeChange}
                    notFoundContent={
                      isEmployeeOptionsLoading ? (
                        'Загрузка исполнителей...'
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Нет доступных исполнителей"
                        />
                      )
                    }
                  />
                </Form.Item>

                <Text className="AddWorkScheduleModal__employee-hint">
                  {selectedEmployeeName
                    ? `Базовое расписание филиала подставлено для ${selectedEmployeeName}`
                    : 'После выбора исполнителя подставим рабочее время филиала'}
                </Text>
              </div>
            ) : (
              <div className="AddWorkScheduleModal__summary-card">
                <Text className="AddWorkScheduleModal__summary-label">
                  Исполнитель
                </Text>
                <Text
                  className="AddWorkScheduleModal__summary-value"
                  data-testid="employee-name"
                >
                  {selectedEmployeeName}
                </Text>
              </div>
            )}
          </div>

          <div className="AddWorkScheduleModal__schedule-card">
            <Text className="AddWorkScheduleModal__section-title">
              Расписание
            </Text>

            <Form.Item
              name="date"
              label="Дата"
              rules={[{ required: true, message: 'Выберите дату' }]}
              className="AddWorkScheduleModal__date-field"
            >
              <DatePicker
                className="AddWorkScheduleModal__date-picker"
                format="DD.MM.YYYY"
                onChange={handleDateChange}
              />
            </Form.Item>

            <div className="AddWorkScheduleModal__time-grid">
              <Form.Item
                name="timeS"
                label="От"
                rules={[{ required: true, message: 'Выберите время начала' }]}
                rootClassName="AddWorkScheduleModal__time-form-item"
              >
                <Select
                  options={timeSelectOptions}
                  placeholder="Начало работы"
                  data-testid="time-start"
                  disabled={isEmployeeOptionsLoading}
                />
              </Form.Item>

              <Form.Item
                name="timeE"
                label="До"
                rules={[
                  { required: true, message: 'Выберите время окончания' },
                ]}
                rootClassName="AddWorkScheduleModal__time-form-item"
              >
                <Select
                  options={timeSelectOptions}
                  placeholder="Конец работы"
                  data-testid="time-end"
                  disabled={isEmployeeOptionsLoading}
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
